'use client';

/**
 * UpgradeModal Component
 *
 * Modal displayed when users attempt to access tier-locked features.
 * Shows value proposition and upgrade CTA.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { SubscriptionTier } from '@ops-matrix/shared-types';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getTierLabel } from '../../providers/PermissionProvider';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton/GlassButton';
import { GlassChip } from '../GlassChip/GlassChip';

// ============================================================================
// Types
// ============================================================================

export interface UpgradeModalProps {
  /** Whether the modal is open */
  open: boolean;

  /** Handler to close the modal */
  onClose: () => void;

  /** Feature that requires upgrade */
  feature?: string;

  /** Required tier to access the feature */
  requiredTier: SubscriptionTier;

  /** Current user's tier */
  currentTier: SubscriptionTier;

  /** Custom description (overrides default) */
  description?: string;

  /** Handler for upgrade button click */
  onUpgrade?: (targetTier: SubscriptionTier) => void;

  /** URL for upgrade page (if no onUpgrade handler) */
  upgradeUrl?: string;

  /** Custom feature comparison data */
  featureComparison?: FeatureComparisonItem[];
}

export interface FeatureComparisonItem {
  /** Feature name */
  name: string;

  /** Availability by tier */
  tiers: Partial<Record<SubscriptionTier, boolean | string>>;
}

// ============================================================================
// Default Feature Comparison
// ============================================================================

const DEFAULT_FEATURE_COMPARISON: FeatureComparisonItem[] = [
  {
    name: 'Users',
    tiers: {
      [SubscriptionTier.FREE]: '2',
      [SubscriptionTier.STARTER]: '5',
      [SubscriptionTier.PROFESSIONAL]: '10',
      [SubscriptionTier.ENTERPRISE]: 'Unlimited',
    },
  },
  {
    name: 'API Calls/month',
    tiers: {
      [SubscriptionTier.FREE]: '1,000',
      [SubscriptionTier.STARTER]: '10,000',
      [SubscriptionTier.PROFESSIONAL]: '50,000',
      [SubscriptionTier.ENTERPRISE]: '500,000',
    },
  },
  {
    name: 'Advanced Reporting',
    tiers: {
      [SubscriptionTier.FREE]: false,
      [SubscriptionTier.STARTER]: false,
      [SubscriptionTier.PROFESSIONAL]: true,
      [SubscriptionTier.ENTERPRISE]: true,
    },
  },
  {
    name: 'Custom Workflows',
    tiers: {
      [SubscriptionTier.FREE]: false,
      [SubscriptionTier.STARTER]: false,
      [SubscriptionTier.PROFESSIONAL]: true,
      [SubscriptionTier.ENTERPRISE]: true,
    },
  },
  {
    name: 'Priority Support',
    tiers: {
      [SubscriptionTier.FREE]: false,
      [SubscriptionTier.STARTER]: false,
      [SubscriptionTier.PROFESSIONAL]: true,
      [SubscriptionTier.ENTERPRISE]: true,
    },
  },
  {
    name: 'SSO/SAML',
    tiers: {
      [SubscriptionTier.FREE]: false,
      [SubscriptionTier.STARTER]: false,
      [SubscriptionTier.PROFESSIONAL]: false,
      [SubscriptionTier.ENTERPRISE]: true,
    },
  },
];

// ============================================================================
// Tier Colors
// ============================================================================

const TIER_COLORS: Record<SubscriptionTier, 'default' | 'primary' | 'warning' | 'error'> = {
  [SubscriptionTier.FREE]: 'default',
  [SubscriptionTier.STARTER]: 'primary',
  [SubscriptionTier.PROFESSIONAL]: 'warning',
  [SubscriptionTier.ENTERPRISE]: 'error',
};

// ============================================================================
// Component
// ============================================================================

/**
 * UpgradeModal
 *
 * Shows upgrade options when users try to access tier-locked features.
 *
 * @example
 * ```tsx
 * <UpgradeModal
 *   open={showUpgrade}
 *   onClose={() => setShowUpgrade(false)}
 *   feature="Advanced Reporting"
 *   requiredTier={SubscriptionTier.PROFESSIONAL}
 *   currentTier={SubscriptionTier.STARTER}
 *   onUpgrade={(tier) => navigate('/billing/upgrade')}
 * />
 * ```
 */
export function UpgradeModal({
  open,
  onClose,
  feature,
  requiredTier,
  currentTier,
  description,
  onUpgrade,
  upgradeUrl = '/control-center/billing',
  featureComparison = DEFAULT_FEATURE_COMPARISON,
}: UpgradeModalProps) {
  const theme = useTheme();
  const { t } = useI18n();

  const handleUpgrade = useCallback(() => {
    if (onUpgrade) {
      onUpgrade(requiredTier);
    } else if (typeof window !== 'undefined') {
      window.location.href = upgradeUrl;
    }
    onClose();
  }, [onUpgrade, requiredTier, upgradeUrl, onClose]);

  // Tiers to display in comparison
  const displayTiers: SubscriptionTier[] = [
    SubscriptionTier.FREE,
    SubscriptionTier.STARTER,
    SubscriptionTier.PROFESSIONAL,
    SubscriptionTier.ENTERPRISE,
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            backgroundImage: 'none',
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            pt: 4,
            pb: 3,
            px: 4,
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(255,183,77,0.1) 0%, rgba(255,152,0,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,183,77,0.15) 0%, rgba(255,152,0,0.08) 100%)',
          }}
        >
          <LockOpenIcon
            sx={{
              fontSize: iconSizes['3xl'],
              color: theme.palette.warning.main,
              mb: 2,
            }}
          />

          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t('upgrade.title')}
          </Typography>

          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {description ??
              (feature
                ? t('upgrade.featureDescription', { feature, tier: getTierLabel(requiredTier) })
                : t('upgrade.generalDescription', { tier: getTierLabel(requiredTier) }))}
          </Typography>

          {/* Current vs Required tier */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mt: 3,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('upgrade.currentPlan')}
              </Typography>
              <Box>
                <GlassChip label={getTierLabel(currentTier)} color={TIER_COLORS[currentTier]} size="small" />
              </Box>
            </Box>

            <Typography
              sx={{
                color: 'text.disabled',
                fontSize: '1.5rem',
              }}
            >
              →
            </Typography>

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('upgrade.requiredPlan')}
              </Typography>
              <Box>
                <GlassChip label={getTierLabel(requiredTier)} color={TIER_COLORS[requiredTier]} size="small" />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Feature comparison table */}
        <Box sx={{ px: 3, py: 2, maxHeight: 300, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('upgrade.feature')}</TableCell>
                {displayTiers.map((tier) => (
                  <TableCell
                    key={tier}
                    align="center"
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        tier === requiredTier
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(255,183,77,0.1)'
                            : 'rgba(255,183,77,0.15)'
                          : 'transparent',
                    }}
                  >
                    {getTierLabel(tier)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {featureComparison.map((row) => (
                <TableRow key={row.name}>
                  <TableCell>{row.name}</TableCell>
                  {displayTiers.map((tier) => {
                    const value = row.tiers[tier];
                    return (
                      <TableCell
                        key={tier}
                        align="center"
                        sx={{
                          backgroundColor:
                            tier === requiredTier
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(255,183,77,0.05)'
                                : 'rgba(255,183,77,0.08)'
                              : 'transparent',
                        }}
                      >
                        {typeof value === 'boolean' ? (
                          value ? (
                            <CheckCircleIcon sx={{ fontSize: iconSizes.md, color: theme.palette.success.main }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: iconSizes.md, color: theme.palette.action.disabled }} />
                          )
                        ) : (
                          <Typography variant="body2">{value ?? '-'}</Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Footer with CTA */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <GlassButton variant="subtle" onClick={onClose}>
            {t('common.maybeLater')}
          </GlassButton>
          <GlassButton variant="gradient" onClick={handleUpgrade}>
            {t('upgrade.cta', { tier: getTierLabel(requiredTier) })}
          </GlassButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Context for programmatic usage
// ============================================================================

interface UpgradeModalContextValue {
  /** Open the upgrade modal */
  openUpgradeModal: (props: Omit<UpgradeModalProps, 'open' | 'onClose'>) => void;

  /** Close the upgrade modal */
  closeUpgradeModal: () => void;

  /** Whether the modal is currently open */
  isOpen: boolean;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

/**
 * Provider for programmatic upgrade modal access
 */
export function UpgradeModalProvider({
  children,
  currentTier = SubscriptionTier.FREE,
}: {
  children: React.ReactNode;
  currentTier?: SubscriptionTier;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState<Omit<UpgradeModalProps, 'open' | 'onClose'>>({
    requiredTier: SubscriptionTier.PROFESSIONAL,
    currentTier,
  });

  const openUpgradeModal = useCallback(
    (props: Omit<UpgradeModalProps, 'open' | 'onClose'>) => {
      setModalProps({ ...props, currentTier: props.currentTier ?? currentTier });
      setIsOpen(true);
    },
    [currentTier],
  );

  const closeUpgradeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      openUpgradeModal,
      closeUpgradeModal,
      isOpen,
    }),
    [openUpgradeModal, closeUpgradeModal, isOpen],
  );

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      <UpgradeModal open={isOpen} onClose={closeUpgradeModal} {...modalProps} />
    </UpgradeModalContext.Provider>
  );
}

/**
 * Hook to access upgrade modal context
 */
export function useUpgradeModal(): UpgradeModalContextValue {
  const context = useContext(UpgradeModalContext);

  if (!context) {
    throw new Error('useUpgradeModal must be used within an UpgradeModalProvider');
  }

  return context;
}

/**
 * Optional hook (returns null if not in provider)
 */
export function useUpgradeModalOptional(): UpgradeModalContextValue | null {
  return useContext(UpgradeModalContext);
}

export default UpgradeModal;
